import {
  abortS3MultipartUpload,
  batchSignS3PartUrls,
  completeS3MultipartUpload,
  createS3FileEntry,
  createS3MultipartUpload,
  getS3UploadedParts,
} from '@app/gen/files';
import {parseApiError} from '@common/http/errors/parsed-api-error';
import {UploadedFile} from '@ui/utils/files/uploaded-file';
import {
  getFromLocalStorage,
  removeFromLocalStorage,
  setInLocalStorage,
} from '@ui/utils/hooks/local-storage';
import {getBootstrapData} from '@ui/bootstrap-data/bootstrap-data-store';
import axios, {AxiosInstance, AxiosProgressEvent} from 'axios';
import axiosRetry from 'axios-retry';
import {
  UploadStrategy,
  UploadStrategyConfigWithBackend,
} from './upload-strategy';

const oneMB = 1024 * 1024;
// Defaults when Settings → Uploading has no value (kept in sync with the backend defaults).
export const DEFAULT_PART_SIZE = 20 * oneMB;
export const DEFAULT_PARALLEL_PARTS = 4;
export const S3_MULTIPART_KEY_PREFIX = 's3-multipart::';

function partSizeSetting(): number {
  const raw = Number(getBootstrapData()?.settings?.uploading?.part_size);
  if (!raw || raw < 5 * oneMB) return DEFAULT_PART_SIZE;
  return Math.min(raw, 512 * oneMB);
}

function parallelPartsSetting(): number {
  const raw = Number(getBootstrapData()?.settings?.uploading?.parallel_parts);
  if (!raw || raw < 1) return DEFAULT_PARALLEL_PARTS;
  return Math.min(Math.floor(raw), 10);
}

// how many urls should be pre-signed per call to backend (at least)
const batchSize = 10;

type ChunkState = {
  blob: Blob | File;
  done: boolean;
  etag?: string;
  partNumber: number;
  bytesUploaded: number;
};

type StoredUrl = {
  createdAt: string;
  uploadId: string;
  fileKey: string;
};

type UploadedPart = Awaited<
  ReturnType<typeof getS3UploadedParts>
>['parts'][number];

type SignedUrl = Awaited<
  ReturnType<typeof batchSignS3PartUrls>
>['urls'][number];

export class S3MultipartUpload implements UploadStrategy {
  private abortController: AbortController;
  private chunks: ChunkState[] = [];
  private uploadId?: string;
  private fileKey?: string;
  private readonly chunkAxios: AxiosInstance;
  private abortedByUser = false;
  private uploadedParts?: UploadedPart[];
  private partRetries = 0;

  get storageKey(): string {
    return `${S3_MULTIPART_KEY_PREFIX}${this.file.fingerprint}`;
  }

  constructor(
    private file: UploadedFile,
    private config: UploadStrategyConfigWithBackend,
  ) {
    this.abortController = new AbortController();
    this.chunkAxios = axios.create();
    axiosRetry(this.chunkAxios, {retries: 3});
  }

  async start() {
    const storedUrl = getFromLocalStorage<StoredUrl>(this.storageKey);
    if (storedUrl) {
      await this.getUploadedParts(storedUrl);
    }

    if (!this.uploadedParts?.length) {
      await this.createMultipartUpload();
      if (!this.uploadId) return;
    }

    this.prepareChunks();

    const result = await this.uploadParts();
    if (result === 'done') {
      const isCompleted = await this.completeMultipartUpload();
      if (!isCompleted) return;

      // catch any errors so below "onError" handler gets executed
      try {
        const response = await this.createFileEntry();
        if (response?.data) {
          this.config.onSuccess?.(response.data, this.file);
          removeFromLocalStorage(this.storageKey);
          return;
        }
      } catch {
        //
      }
    }

    // upload failed
    if (!this.abortController.signal.aborted) {
      this.abortController.abort();
    }
    if (!this.abortedByUser) {
      this.config.onError?.(null, this.file);
    }
  }

  // Pause: stop sending parts, keep the multipart upload so it can resume.
  async abort() {
    this.abortedByUser = true;
    this.abortController.abort();
  }

  // Cancel: stop and delete the multipart upload (and its parts) on S3.
  async cancel() {
    this.abortedByUser = true;
    this.abortController.abort();
    const stored = getFromLocalStorage<StoredUrl>(this.storageKey);
    const key = this.fileKey ?? stored?.fileKey;
    const uploadId = this.uploadId ?? stored?.uploadId;
    removeFromLocalStorage(this.storageKey);
    if (key && uploadId) {
      await abortS3MultipartUpload({
        key,
        uploadId,
        uploadType: this.config.uploadType,
        backendId: this.config.backendId,
      }).catch(() => {});
    }
  }

  /**
   * Uploads pending parts with a pool of parallel workers. URLs are signed in
   * batches just ahead of the workers; failed parts are retried with backoff.
   */
  private async uploadParts(): Promise<'done' | undefined> {
    const parallel = parallelPartsSetting();
    const signBatch = Math.max(batchSize, parallel * 2);

    while (true) {
      if (this.abortController.signal.aborted) return;
      const pendingChunks = this.chunks.filter(c => !c.done);
      if (!pendingChunks.length) {
        return 'done';
      }

      const signedUrls = await this.batchSignUrls(
        pendingChunks.slice(0, signBatch),
      );
      if (!signedUrls) return;

      const queue = [...signedUrls];
      let failed = false;
      const worker = async () => {
        while (queue.length && !this.abortController.signal.aborted) {
          const item = queue.shift()!;
          const ok = await this.uploadPartToS3(item);
          if (!ok) {
            failed = true;
          }
        }
      };
      await Promise.all(
        Array.from({length: Math.min(parallel, signedUrls.length)}, worker),
      );

      if (this.abortController.signal.aborted) return;
      if (failed) {
        this.partRetries += 1;
        if (this.partRetries > 8) return;
        // 1s, 2s, 4s … capped at 20s between rounds of failed parts.
        const delay = Math.min(20000, 1000 * 2 ** (this.partRetries - 1));
        await new Promise(resolve => setTimeout(resolve, delay));
      } else {
        this.partRetries = 0;
      }
    }
  }

  private async batchSignUrls(batch: ChunkState[]) {
    const response = await batchSignS3PartUrls({
      partNumbers: batch.map(i => i.partNumber),
      uploadId: this.uploadId!,
      key: this.fileKey!,
      uploadType: this.config.uploadType,
      backendId: this.config.backendId,
    }).catch(() => {
      if (!this.abortController.signal.aborted) {
        this.abortController.abort();
      }
    });

    return response?.urls;
  }

  private async uploadPartToS3({
    url,
    partNumber,
  }: SignedUrl): Promise<boolean | void> {
    const chunk = this.chunks.find(c => c.partNumber === partNumber);
    if (!chunk) return;
    return this.chunkAxios
      .put(url, chunk.blob, {
        withCredentials: false,
        adapter: 'xhr',
        signal: this.abortController.signal,
        onUploadProgress: (e: AxiosProgressEvent) => {
          if (!e.event.lengthComputable) return;

          chunk.bytesUploaded = e.loaded;
          const totalUploaded = this.chunks.reduce(
            (n, c) => n + c.bytesUploaded,
            0,
          );

          this.config.onProgress?.({
            bytesUploaded: totalUploaded,
            bytesTotal: this.file.size,
          });
        },
      })
      .then(r => {
        const etag = r.headers.etag;
        if (etag) {
          chunk.done = true;
          chunk.etag = etag;
          return true;
        }
        return false;
      })
      .catch(() => {
        // Progress of a failed part starts again from zero.
        chunk.bytesUploaded = 0;
        return false;
      });
  }

  private async createMultipartUpload(): Promise<void> {
    const response = await createS3MultipartUpload({
      clientName: this.file.name,
      clientMime: this.file.mime,
      clientSize: this.file.size,
      clientExtension: this.file.extension,
      uploadType: this.config.uploadType,
      backendId: this.config.backendId,
      ...this.config.metadata,
    }).catch(err => {
      if (err.code !== 'ERR_CANCELED') {
        const parsedError = parseApiError(err);
        this.config.onError?.(parsedError.message, this.file);
      }
    });

    if (response) {
      this.uploadId = response.uploadId;
      this.fileKey = response.key;
      setInLocalStorage(this.storageKey, {
        createdAt: new Date().toISOString(),
        fileKey: this.fileKey,
        uploadId: this.uploadId,
      } as StoredUrl);
    }
  }

  private async getUploadedParts({fileKey, uploadId}: StoredUrl) {
    const response = await getS3UploadedParts({
      key: fileKey,
      uploadId,
      uploadType: this.config.uploadType,
      backendId: this.config.backendId,
    }).catch(() => {
      removeFromLocalStorage(this.storageKey);
      return null;
    });
    if (response?.parts?.length) {
      this.uploadedParts = response.parts;
      this.uploadId = uploadId;
      this.fileKey = fileKey;
    }
  }

  private async completeMultipartUpload() {
    if (!this.fileKey || !this.uploadId) {
      return null;
    }

    return completeS3MultipartUpload({
      key: this.fileKey,
      uploadId: this.uploadId,
      uploadType: this.config.uploadType,
      backendId: this.config.backendId,
      parts: this.chunks.map(c => ({
        ETag: c.etag!,
        PartNumber: c.partNumber!,
      })),
    }).catch(() => {
      this.config.onError?.(null, this.file);
      return null;
    });
  }

  private async createFileEntry() {
    return await createS3FileEntry({
      ...this.config.metadata,
      clientMime: this.file.mime,
      clientName: this.file.name,
      clientSize: this.file.size,
      clientExtension: this.file.extension,
      filename: this.fileKey!.split('/').pop()!,
      uploadType: this.config.uploadType,
      backendId: this.config.backendId,
    }).catch();
  }

  private prepareChunks() {
    this.chunks = [];
    // at least 5MB per request, at most 10k requests
    const minChunkSize = Math.max(5 * oneMB, Math.ceil(this.file.size / 10000));
    const chunkSize = Math.max(partSizeSetting(), minChunkSize);

    // Upload zero-sized files in one zero-sized chunk
    if (this.file.size === 0) {
      this.chunks.push({
        blob: this.file.native,
        done: false,
        partNumber: 1,
        bytesUploaded: 0,
      });
    } else {
      let partNumber = 1;
      for (let i = 0; i < this.file.size; i += chunkSize) {
        const end = Math.min(this.file.size, i + chunkSize);
        // check if this part was already uploaded previously
        const previouslyUploaded = this.uploadedParts?.find(
          p => p.PartNumber === partNumber,
        );
        this.chunks.push({
          blob: this.file.native.slice(i, end),
          done: !!previouslyUploaded,
          partNumber,
          etag: previouslyUploaded ? previouslyUploaded.ETag : undefined,
          bytesUploaded: previouslyUploaded?.Size
            ? previouslyUploaded?.Size
            : 0,
        });
        partNumber++;
      }
    }
  }

  static async create(
    file: UploadedFile,
    config: UploadStrategyConfigWithBackend,
  ): Promise<S3MultipartUpload> {
    return new S3MultipartUpload(file, config);
  }
}
